import { createContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

/**
 * Who is signed in, for the whole app.
 *
 * Three states, not two: `loading` exists because the session is read from
 * storage asynchronously, and a screen that treats "not yet known" as "signed
 * out" will flash a sign-in form at someone who is already authenticated — or
 * worse, redirect them away from the page they asked for.
 *
 * The session is per domain by construction: `lib/supabase.ts` keys storage by
 * hostname, so numtable.com and procalendar.app hold independent sessions
 * against the same Supabase project (docs/ARCHITECTURE.md §2). This provider
 * makes no attempt to bridge them, and must not.
 */
export type SessionState =
  | { readonly status: 'loading'; readonly session: null }
  | { readonly status: 'signedIn'; readonly session: Session }
  | { readonly status: 'signedOut'; readonly session: null };

export const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({
    status: 'loading',
    session: null,
  });

  useEffect(() => {
    let active = true;

    // onAuthStateChange fires with the restored session on subscribe, but not
    // reliably before the first paint, so the initial read stays explicit.
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setState(
        data.session
          ? { status: 'signedIn', session: data.session }
          : { status: 'signedOut', session: null },
      );
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!active) return;
        setState(
          session
            ? { status: 'signedIn', session }
            : { status: 'signedOut', session: null },
        );
      },
    );

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}
